import type { ErrorEvent, EventHint } from '@sentry/ember';
import config from 'uplisting-frontend/config/environment';

// Sentry resolves an event's URL from the innermost stack frame, so errors
// raised inside the cross-origin React remote are dropped by `allowUrls`
// unless the origin it is served from is allowed too.
function reactRemoteOrigins(): string[] {
  const { entry } = config.reactRemote;

  if (!entry) {
    return [];
  }

  try {
    return [new URL(entry).origin];
  } catch {
    return [];
  }
}

export async function startSentry(): Promise<void> {
  if (config.IS_TEST) {
    return;
  }

  window.Sentry = await import('@sentry/ember');

  window.Sentry.init({
    dsn: config.IS_PROD ? config.sentry.dsn : undefined,
    integrations: [
      window.Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      window.Sentry.browserTracingIntegration(),
      window.Sentry.extraErrorDataIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.01, // Capture 1% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      /^https:\/\/app\.staging-uplisting\.io/,
      /^https:\/\/app\.uplisting\.io/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    normalizeDepth: 10,

    debug: false,
    // debug: config.IS_DEV,

    environment: config.environment,
    release: config.sentry.release,
    allowUrls: [
      /^https:\/\/app\.staging-uplisting\.io/,
      /^https:\/\/app\.uplisting\.io/,
      ...reactRemoteOrigins(),
    ],

    denyUrls: [
      /https?:\/\/((cdn|www)\.)?segment\.com/,
      /https?:\/\/((cdn|www)\.)?google\.com/,
      /https?:\/\/((cdn|maps)\.)?googleapis\.com/,
      /extensions\//i,
      /^chrome:\/\//i,
    ],

    beforeSend(event: ErrorEvent, hint: EventHint) {
      const error = hint.originalException;

      const ignoreErrors = [
        /NetworkError when attempting to fetch resource/,
        /Failed to fetch/,
        /Load failed/,
        /Refused to evaluate/,
        /Refused to load/,
        /Failed to load resource/,
        /Non-Error exception captured/,
        /Non-Error promise rejection captured/,
        /captured as promise rejection/,
        /Request Rejected with an Unknown Error/,
        /CustomEvent/,
        /InvalidStateError/,
        /view transition/,
        /ViewTransition/,
        /Transition was skipped/,
        /transition was skipped/,
        /Transition was aborted/,
        /transition was aborted/,
        /disconnected popover elements/,
      ];

      // Ignoring these errors due to https://github.com/emberjs/ember.js/issues/12505
      // and https://github.com/emberjs/ember.js/issues/18416
      // @ts-expect-error - ignoring unknown TS typings
      const isTransitionAbort = error?.name === 'TransitionAborted';

      const errorMessage =
        // @ts-expect-error - ignoring unknown TS typings
        error?.message || event.exception?.values?.[0]?.value || '';

      const isIgnorable = ignoreErrors.some((re) => re.test(errorMessage));

      if (isTransitionAbort || isIgnorable) {
        return null;
      }

      if (event.exception?.values?.[0]?.value === 'undefined') {
        // try to get more context from the stack trace
        const stackTrace = event.exception.values[0].stacktrace;

        if (stackTrace) {
          event.exception.values[0].value = `Undefined error at ${stackTrace.frames?.[0]?.filename}:${stackTrace.frames?.[0]?.lineno}`;
        }
      }

      // Mirror non-ignored frontend errors into the analytics friction
      // dashboard (UPL-8232). `window.analytics` only exists in production,
      // where Segment is loaded, so this is a no-op elsewhere.
      window.analytics?.track?.('app-error', {
        error_type: 'exception',
        message:
          errorMessage ||
          event.exception?.values?.[0]?.value ||
          'Unknown error',
        route: window.location.pathname,
        severity: event.level ?? 'error',
      });

      return event;
    },
  });
}
